import type { TaskStatusOutput } from "@orsane/schema/tasks";
import { cva } from "class-variance-authority";
import type * as React from "react";
import { statusColors } from "@/app/colors";
import { cn } from "@/lib/utils";

const taskBadgeVariants = cva(
    "inline-flex items-center rounded-md border px-1.5 py-0.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-950 focus:ring-offset-2 dark:border-neutral-800 dark:focus:ring-neutral-300",
    {
        variants: {
            variant: {
                todo: "border-transparent bg-transparent text-transparent group-hover/statuscolumn:bg-white group-hover/statuscolumn:text-zinc-900 dark:bg-neutral-50 dark:text-zinc-900 dark:hover:bg-neutral-50/80",
                completed: "border-transparent bg-emerald-200 text-emerald-900",
                default: "border-transparent bg-neutral-100 text-zinc-700",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    }
);

export interface TaskBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
    status: TaskStatusOutput | null | undefined;
    hideTodo?: boolean;
}

function TaskBadge({ status, className, hideTodo = false, ...props }: TaskBadgeProps) {
    const colorKey = status?.color;
    let colorClassName: string | false = false;

    const variant = status?.isDefault && hideTodo ? "todo" : status?.isCompleted ? "completed" : "default";
    if (colorKey) {
        colorClassName = !status?.isDefault && !status?.isCompleted && statusColors[colorKey as keyof typeof statusColors]?.className;
    }

    return (
        <div data-colorkey={colorKey} className={cn(taskBadgeVariants({ variant }), colorClassName, className)} {...props}>
            {status?.name}
        </div>
    );
}

export { TaskBadge, taskBadgeVariants };
